import { aimooT } from "@utils/aimoo-i18n";
import {
  editCombined,
  enableNsfw,
  getUncombinedReport,
  setIsoData,
  toast,
} from "@utils/app";
import { resourcesSettled } from "@utils/helpers";
import { scrollMixin } from "../mixins/scroll-mixin";
import { amAdmin } from "@utils/roles";
import { ItemIdAndRes, itemLoading, RouteDataResponse } from "@utils/types";
import { Component, InfernoNode } from "inferno";
import {
  CommentReportResponse,
  GetSiteResponse,
  LemmyHttp,
  ListReports,
  PagedResponse,
  PostReportResponse,
  PrivateMessageReportResponse,
  ReportCombinedView,
  ResolveCommentReport,
  ResolvePostReport,
  ResolvePrivateMessageReport,
  PaginationCursor,
  ResolveCommunityReport,
  CommunityReportResponse,
  ReportType,
  RemovePost,
  RemoveComment,
  Person,
  Community,
  CommentReportId,
  PostReportId,
  PrivateMessageReportId,
  CommunityReportId,
} from "lemmy-js-client";
import { InitialFetchRequest } from "@utils/types";
import { FirstLoadService, HttpService, I18NextService } from "../../services";
import {
  EMPTY_REQUEST,
  LOADING_REQUEST,
  RequestState,
  wrapClient,
} from "../../services/HttpService";
import { CommentReport } from "../comment/comment-report";
import { HtmlTags } from "../common/html-tags";
import { Icon, Spinner } from "../common/icon";
import { PostReport } from "../post/post-report";
import { PrivateMessageReport } from "../private_message/private-message-report";
import { UnreadCounterService } from "../../services";
import { getHttpBaseInternal } from "../../utils/env";
import { Link, RouteComponentProps, RouterContext } from "inferno-router";
import { IRoutePropsWithFetch } from "@utils/routes";
import { isBrowser } from "@utils/browser";
import { PaginatorCursor } from "../common/paginator-cursor";
import { CommunityReport } from "../community/community-report";
import ModActionFormModal, {
  BanUpdateForm,
} from "@components/common/modal/mod-action-form-modal";
import { futureDaysToUnixTime } from "@utils/date";
import {
  FilterChipDropdown,
  FilterOption,
} from "@components/common/filter-chip-dropdown";
import { FilterChipCheckbox } from "@components/common/filter-chip-checkbox";
import classNames from "classnames";

type ReportsData = RouteDataResponse<{
  reportsRes: PagedResponse<ReportCombinedView>;
}>;

interface ReportsState {
  reportsRes: RequestState<PagedResponse<ReportCombinedView>>;
  commentResolveRes: ItemIdAndRes<CommentReportId, CommentReportResponse>;
  postResolveRes: ItemIdAndRes<PostReportId, PostReportResponse>;
  pmResolveRes: ItemIdAndRes<
    PrivateMessageReportId,
    PrivateMessageReportResponse
  >;
  communityResolveRes: ItemIdAndRes<CommunityReportId, CommunityReportResponse>;
  showUnresolvedOnly: boolean;
  reportType: ReportType;
  siteRes: GetSiteResponse;
  cursor?: PaginationCursor;
  isIsomorphic: boolean;
  banFromCommunityForm?: BanFromCommunityData;
  adminBanForm?: BanFromSiteData;
  showCommunityRuleViolations: boolean;
}

type ReportsRouteProps = RouteComponentProps<Record<string, never>> &
  Record<string, never>;
export type ReportsFetchConfig = IRoutePropsWithFetch<
  ReportsData,
  Record<string, never>,
  Record<string, never>
>;

const reportTypeOptions: FilterOption<ReportType>[] = [
  { value: "all", i18n: "all" },
  { value: "posts", i18n: "posts" },
  { value: "comments", i18n: "comments" },
  { value: "private_messages", i18n: "messages" },
  { value: "communities", i18n: "communities" },
];

// These are needed because ModActionFormModal requires full Person/Community, but the api forms
// (BanFromCommunity) only contain PersonId/CommunityId.
export interface BanFromCommunityData {
  person: Person;
  community: Community;
  ban: boolean;
}

export interface BanFromSiteData {
  person: Person;
  ban: boolean;
}

@scrollMixin
export class Reports extends Component<ReportsRouteProps, ReportsState> {
  private isoData = setIsoData<ReportsData>(this.context);
  state: ReportsState = {
    reportsRes: EMPTY_REQUEST,
    commentResolveRes: { id: 0, res: EMPTY_REQUEST },
    postResolveRes: { id: 0, res: EMPTY_REQUEST },
    pmResolveRes: { id: 0, res: EMPTY_REQUEST },
    communityResolveRes: { id: 0, res: EMPTY_REQUEST },
    showUnresolvedOnly: true,
    reportType: "all",
    siteRes: this.isoData.siteRes,
    isIsomorphic: false,
    showCommunityRuleViolations: false,
  };

  loadingSettled() {
    return resourcesSettled([this.state.reportsRes]);
  }

  constructor(props: ReportsRouteProps, context: object) {
    super(props, context);

    // Only fetch the data if coming from another route
    if (FirstLoadService.isFirstLoad) {
      const { reportsRes } = this.isoData.routeData;

      this.state = {
        ...this.state,
        reportsRes,
        isIsomorphic: true,
      };
    }
  }

  async componentWillMount() {
    if (!this.state.isIsomorphic && isBrowser()) {
      await this.refetch();
    }
  }

  get documentTitle(): string {
    const mui = this.isoData.myUserInfo;
    return mui
      ? `@${mui.local_user_view.person.name} ${I18NextService.i18n.t(
          "reports",
        )} - ${this.state.siteRes.site_view.site.name}`
      : "";
  }

  get nextPageCursor(): PaginationCursor | undefined {
    const { reportsRes: res } = this.state;
    return res.state === "success" ? res.data.next_page : undefined;
  }

  render() {
    const banFromCommunityForm = this.state.banFromCommunityForm;
    const adminBanForm = this.state.adminBanForm;
    const count =
      this.state.reportsRes.state === "success"
        ? this.state.reportsRes.data.items.length
        : undefined;

    return (
      <div className="aimoo-reports person-reports">
        {banFromCommunityForm && (
          <ModActionFormModal
            onSubmit={form => handleSubmitBanFromCommunity(this, form)}
            modActionType="community-ban"
            creator={banFromCommunityForm.person}
            community={banFromCommunityForm.community}
            isBanned={!banFromCommunityForm.ban}
            onCancel={() => handleCloseModActionModals(this)}
            show
            loading={false}
          />
        )}
        {adminBanForm && (
          <ModActionFormModal
            onSubmit={form => handleSubmitAdminBan(this, form)}
            modActionType="site-ban"
            creator={adminBanForm.person}
            isBanned={!adminBanForm.ban}
            onCancel={() => handleCloseModActionModals(this)}
            show
            loading={false}
          />
        )}
        <HtmlTags
          title={this.documentTitle}
          context={this.context as RouterContext}
        />
        <div className="aimoo-reports__inner container-lg px-3">
          <nav className="aimoo-reports__breadcrumb" aria-label="breadcrumb">
            <Link to="/" className="aimoo-reports__breadcrumb-link">
              {aimooT("community")}
            </Link>
            <span className="aimoo-reports__breadcrumb-sep" aria-hidden="true">
              /
            </span>
            <span className="aimoo-reports__breadcrumb-current">
              {aimooT("reports_title")}
            </span>
          </nav>

          <header className="aimoo-reports__header">
            <div>
              <h1 className="aimoo-reports__title">{aimooT("reports_title")}</h1>
              <p className="aimoo-reports__subtitle">
                {aimooT("reports_subtitle")}
              </p>
            </div>
            {typeof count === "number" && (
              <span
                className={classNames("aimoo-reports__count-badge", {
                  "aimoo-reports__count-badge--empty": count === 0,
                })}
              >
                {this.state.showUnresolvedOnly
                  ? aimooT("pending_count", { count })
                  : aimooT("page_count", { count })}
              </span>
            )}
          </header>

          <div className="aimoo-reports__layout">
            <section className="aimoo-reports__main">
              <div className="aimoo-reports__toolbar">{this.selects()}</div>
              <div className="aimoo-reports__list">{this.section}</div>
              <div className="aimoo-reports__pager">
                <PaginatorCursor
                  current={this.state.cursor}
                  resource={this.state.reportsRes}
                  onPageChange={cursor => handlePageChange(this, cursor)}
                />
              </div>
            </section>

            <aside className="aimoo-reports__aside">
              {this.renderTipsCard()}
              {this.renderProcessCard()}
            </aside>
          </div>
        </div>
      </div>
    );
  }

  renderTipsCard(): InfernoNode {
    return (
      <div className="aimoo-reports__side-card aimoo-reports__side-card--note">
        <div className="aimoo-reports__side-note-head">
          <Icon icon="alert-triangle" classes="icon-inline" />
          <h2 className="aimoo-reports__side-title">
            {aimooT("report_tips_title")}
          </h2>
        </div>
        <ul className="aimoo-reports__tips">
          <li>{aimooT("report_tip_1")}</li>
          <li>{aimooT("report_tip_2")}</li>
          <li>{aimooT("report_tip_3")}</li>
          <li>{aimooT("report_tip_4")}</li>
        </ul>
      </div>
    );
  }

  renderProcessCard(): InfernoNode {
    const steps = [
      {
        title: aimooT("report_flow_1_title"),
        desc: aimooT("report_flow_1_desc"),
      },
      {
        title: aimooT("report_flow_2_title"),
        desc: aimooT("report_flow_2_desc"),
      },
      {
        title: aimooT("report_flow_3_title"),
        desc: aimooT("report_flow_3_desc"),
      },
      {
        title: aimooT("report_flow_4_title"),
        desc: aimooT("report_flow_4_desc"),
      },
    ];
    return (
      <div className="aimoo-reports__side-card">
        <h2 className="aimoo-reports__side-title">
          {aimooT("report_flow_title")}
        </h2>
        <ol className="aimoo-reports__steps">
          {steps.map((step, index) => (
            <li key={step.title}>
              <span className="aimoo-reports__step-num">{index + 1}</span>
              <span className="aimoo-reports__step-text">
                <span className="aimoo-reports__step-title">{step.title}</span>
                <span className="aimoo-reports__step-desc">{step.desc}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  get section() {
    switch (this.state.reportType) {
      case "all": {
        return this.all();
      }
      case "comments": {
        return this.commentReports();
      }
      case "posts": {
        return this.postReports();
      }
      case "private_messages": {
        return this.privateMessageReports();
      }
      case "communities": {
        return this.communityReports();
      }

      default: {
        return null;
      }
    }
  }

  reportTypeFilters() {
    // Only show communities and private messages if you're an admin.
    const options = amAdmin(this.isoData.myUserInfo)
      ? reportTypeOptions
      : reportTypeOptions.filter(
          v => !["private_messages", "communities"].includes(v.value),
        );

    return (
      <FilterChipDropdown
        label={"type"}
        allOptions={options}
        currentOption={options.find(t => t.value === this.state.reportType)}
        onSelect={val => handleReportTypeChange(this, val)}
      />
    );
  }

  selects() {
    return (
      <div className="aimoo-reports__filters">
        <FilterChipCheckbox
          option={"show_unresolved_only"}
          isChecked={this.state.showUnresolvedOnly}
          onCheck={val => handleShowUnresolvedOnlyChange(this, val)}
        />
        {this.reportTypeFilters()}
        {this.isoData.myUserInfo?.local_user_view.local_user.admin && (
          <FilterChipCheckbox
            option={"show_community_reports"}
            isChecked={this.state.showCommunityRuleViolations ?? false}
            onCheck={val => handleClickshowCommunityReports(this, val)}
          />
        )}
      </div>
    );
  }

  renderItemType(i: ReportCombinedView): InfernoNode {
    const siteRes = this.state.siteRes;
    const cardClass = classNames(
      "aimoo-reports__card",
      `aimoo-reports__card--${i.type_}`,
      {
        "aimoo-reports__card--resolved": isReportResolved(i),
      },
    );
    const typeLabel = reportTypeLabel(i.type_);

    let body: InfernoNode;
    switch (i.type_) {
      case "comment":
        body = (
          <CommentReport
            key={i.type_ + i.comment_report.id}
            report={i}
            myUserInfo={this.isoData.myUserInfo}
            localSite={siteRes.site_view.local_site}
            admins={this.isoData.siteRes.admins}
            loading={
              itemLoading(this.state.commentResolveRes) === i.comment_report.id
            }
            onResolveReport={form => handleResolveCommentReport(this, form)}
            onRemoveComment={form => handleRemoveComment(this, form)}
            onAdminBan={form => handleAdminBan(this, form)}
            onModBanFromCommunity={form =>
              handleModBanFromCommunity(this, form)
            }
          />
        );
        break;
      case "post":
        body = (
          <PostReport
            key={i.type_ + i.post_report.id}
            report={i}
            enableNsfw={enableNsfw(siteRes)}
            showAdultConsentModal={this.isoData.showAdultConsentModal}
            myUserInfo={this.isoData.myUserInfo}
            localSite={siteRes.site_view.local_site}
            admins={this.isoData.siteRes.admins}
            loading={
              itemLoading(this.state.postResolveRes) === i.post_report.id
            }
            onResolveReport={form => handleResolvePostReport(this, form)}
            onRemovePost={form => handleRemovePost(this, form)}
            onAdminBan={form => handleAdminBan(this, form)}
            onModBanFromCommunity={form =>
              handleModBanFromCommunity(this, form)
            }
          />
        );
        break;
      case "private_message":
        body = (
          <PrivateMessageReport
            key={i.type_ + i.private_message_report.id}
            report={i}
            onResolveReport={form =>
              handleResolvePrivateMessageReport(this, form)
            }
            loading={
              itemLoading(this.state.pmResolveRes) ===
              i.private_message_report.id
            }
            myUserInfo={this.isoData.myUserInfo}
          />
        );
        break;
      case "community":
        body = (
          <CommunityReport
            key={i.type_ + i.community_report.id}
            report={i}
            loading={
              itemLoading(this.state.communityResolveRes) ===
              i.community_report.id
            }
            onResolveReport={form => handleResolveCommunityReport(this, form)}
            myUserInfo={this.isoData.myUserInfo}
          />
        );
        break;
      default:
        return null;
    }

    return (
      <article
        key={`${i.type_}-${reportItemId(i)}`}
        className={cardClass}
      >
        <div className="aimoo-reports__card-top">
          <span className="aimoo-reports__type-badge">{typeLabel}</span>
          <span
            className={classNames("aimoo-reports__status-badge", {
              "aimoo-reports__status-badge--resolved": isReportResolved(i),
            })}
          >
            {isReportResolved(i) ? aimooT("resolved") : aimooT("unresolved")}
          </span>
        </div>
        {body}
      </article>
    );
  }

  all(): InfernoNode | void {
    switch (this.state.reportsRes.state) {
      case "loading":
        return this.renderLoading();
      case "success": {
        const items = this.state.reportsRes.data.items;
        if (items.length === 0) {
          return this.renderEmpty();
        }
        return (
          <div className="aimoo-reports__cards">
            {items.map(i => this.renderItemType(i))}
          </div>
        );
      }
    }
  }

  commentReports(): InfernoNode | void {
    return this.renderFiltered("comment");
  }

  postReports(): InfernoNode | void {
    return this.renderFiltered("post");
  }

  privateMessageReports(): InfernoNode | void {
    return this.renderFiltered("private_message");
  }

  communityReports(): InfernoNode | void {
    return this.renderFiltered("community");
  }

  renderFiltered(
    type_: ReportCombinedView["type_"],
  ): InfernoNode | void {
    const res = this.state.reportsRes;
    switch (res.state) {
      case "loading":
        return this.renderLoading();
      case "success": {
        const reports = res.data.items.filter(r => r.type_ === type_);
        if (reports.length === 0) {
          return this.renderEmpty();
        }
        return (
          <div className="aimoo-reports__cards">
            {reports.map(i => this.renderItemType(i))}
          </div>
        );
      }
    }
  }

  renderLoading(): InfernoNode {
    return (
      <div className="aimoo-reports__loading">
        <Spinner large />
        <span>{aimooT("loading_reports")}</span>
      </div>
    );
  }

  renderEmpty(): InfernoNode {
    return (
      <div className="aimoo-reports__empty">
        <Icon icon="check" classes="icon-inline" />
        <p className="aimoo-reports__empty-title">{aimooT("no_reports")}</p>
        <p className="aimoo-reports__empty-desc">
          {this.state.showUnresolvedOnly
            ? aimooT("no_pending_reports")
            : aimooT("try_other_filter")}
        </p>
      </div>
    );
  }

  static fetchInitialData = async ({
    headers,
  }: InitialFetchRequest): Promise<ReportsData> => {
    const client = wrapClient(
      new LemmyHttp(getHttpBaseInternal(), { headers }),
    );
    const unresolved_only = true;

    const reportsForm: ListReports = {
      unresolved_only,
      show_community_rule_violations: false,
    };

    return {
      reportsRes: await client.listReports(reportsForm),
    };
  };

  refetchToken?: symbol;
  async refetch() {
    const token = (this.refetchToken = Symbol());
    const cursor = this.state.cursor;

    this.setState({
      reportsRes: LOADING_REQUEST,
    });

    const form: ListReports = {
      unresolved_only: this.state.showUnresolvedOnly,
      type_: this.state.reportType,
      show_community_rule_violations: this.state.showCommunityRuleViolations,
      page_cursor: cursor,
    };

    const reportPromise = HttpService.client
      .listReports(form)
      .then(reportsRes => {
        if (token === this.refetchToken) {
          this.setState({ reportsRes });
        }
      });

    await Promise.all([reportPromise]);
  }

  findAndUpdateCommentReport(res: RequestState<CommentReportResponse>) {
    this.setState(s => {
      if (s.reportsRes.state === "success" && res.state === "success") {
        s.reportsRes.data.items = editCombined(
          { type_: "comment", ...res.data.comment_report_view },
          s.reportsRes.data.items,
          getUncombinedReport,
        );
      }
      return s;
    });
  }

  findAndUpdatePostReport(res: RequestState<PostReportResponse>) {
    this.setState(s => {
      if (s.reportsRes.state === "success" && res.state === "success") {
        s.reportsRes.data.items = editCombined(
          { type_: "post", ...res.data.post_report_view },
          s.reportsRes.data.items,
          getUncombinedReport,
        );
      }
      return s;
    });
  }

  findAndUpdatePrivateMessageReport(
    res: RequestState<PrivateMessageReportResponse>,
  ) {
    this.setState(s => {
      if (s.reportsRes.state === "success" && res.state === "success") {
        s.reportsRes.data.items = editCombined(
          { type_: "private_message", ...res.data.private_message_report_view },
          s.reportsRes.data.items,
          getUncombinedReport,
        );
      }
      return s;
    });
  }

  findAndUpdateCommunityReport(res: RequestState<CommunityReportResponse>) {
    this.setState(s => {
      if (s.reportsRes.state === "success" && res.state === "success") {
        s.reportsRes.data.items = editCombined(
          { type_: "community", ...res.data.community_report_view },
          s.reportsRes.data.items,
          getUncombinedReport,
        );
      }
      return s;
    });
  }

  async update() {
    await UnreadCounterService.Instance.updateUnreadCounts();
    await this.refetch();
  }
}

async function handlePageChange(i: Reports, cursor?: PaginationCursor) {
  i.setState({ cursor });
  await i.refetch();
}

async function handleShowUnresolvedOnlyChange(
  i: Reports,
  showUnresolvedOnly: boolean,
) {
  i.setState({
    showUnresolvedOnly,
    cursor: undefined,
  });
  await i.refetch();
}

async function handleReportTypeChange(i: Reports, val: ReportType) {
  i.setState({
    reportType: val,
    cursor: undefined,
  });
  await i.refetch();
}

async function handleResolveCommentReport(
  i: Reports,
  form: ResolveCommentReport,
) {
  i.setState({
    commentResolveRes: { id: form.report_id, res: LOADING_REQUEST },
  });
  const res = await HttpService.client.resolveCommentReport(form);
  i.setState({ commentResolveRes: { id: form.report_id, res } });
  i.findAndUpdateCommentReport(res);
  await i.update();
}

async function handleResolvePostReport(i: Reports, form: ResolvePostReport) {
  i.setState({
    postResolveRes: { id: form.report_id, res: LOADING_REQUEST },
  });
  const res = await HttpService.client.resolvePostReport(form);
  i.setState({
    postResolveRes: { id: form.report_id, res },
  });
  i.findAndUpdatePostReport(res);
  await i.update();
}

async function handleRemovePost(i: Reports, form: RemovePost) {
  await HttpService.client.removePost(form);
  await i.update();
}

async function handleRemoveComment(i: Reports, form: RemoveComment) {
  await HttpService.client.removeComment(form);
  await i.update();
}

function handleModBanFromCommunity(i: Reports, form: BanFromCommunityData) {
  i.setState({ banFromCommunityForm: form });
}

function handleAdminBan(i: Reports, form: BanFromSiteData) {
  i.setState({ adminBanForm: form });
}

async function handleResolvePrivateMessageReport(
  i: Reports,
  form: ResolvePrivateMessageReport,
) {
  i.setState({
    pmResolveRes: { id: form.report_id, res: LOADING_REQUEST },
  });
  const res = await HttpService.client.resolvePrivateMessageReport(form);
  i.setState({
    pmResolveRes: { id: form.report_id, res },
  });
  i.findAndUpdatePrivateMessageReport(res);

  await i.update();
}

async function handleResolveCommunityReport(
  i: Reports,
  form: ResolveCommunityReport,
) {
  i.setState({
    communityResolveRes: { id: form.report_id, res: LOADING_REQUEST },
  });
  const res = await HttpService.client.resolveCommunityReport(form);
  i.setState({
    communityResolveRes: { id: form.report_id, res },
  });
  // TODO
  toast("Not implemented");
  i.findAndUpdateCommunityReport(res);
  await i.update();
}

async function handleSubmitBanFromCommunity(i: Reports, form: BanUpdateForm) {
  const banFromCommunityForm = i.state.banFromCommunityForm;
  if (banFromCommunityForm) {
    await HttpService.client.banFromCommunity({
      person_id: banFromCommunityForm.person.id,
      community_id: banFromCommunityForm.community.id,
      ban: banFromCommunityForm.ban,
      expires_at: futureDaysToUnixTime(form.daysUntilExpires),
      reason: form.reason,
    });
    i.setState({ banFromCommunityForm: undefined });
    await i.update();
  }
}

async function handleSubmitAdminBan(i: Reports, form: BanUpdateForm) {
  const adminBanForm = i.state.adminBanForm;
  if (adminBanForm) {
    await HttpService.client.banPerson({
      person_id: adminBanForm.person.id,
      ban: adminBanForm.ban,
      expires_at: futureDaysToUnixTime(form.daysUntilExpires),
      reason: form.reason,
    });
    i.setState({ adminBanForm: undefined });
    await i.update();
  }
}

function handleCloseModActionModals(i: Reports) {
  i.setState({
    adminBanForm: undefined,
    banFromCommunityForm: undefined,
  });
}

async function handleClickshowCommunityReports(i: Reports, val: boolean) {
  i.setState({
    showCommunityRuleViolations: val,
  });
  await i.update();
}

function isReportResolved(i: ReportCombinedView): boolean {
  switch (i.type_) {
    case "comment":
      return i.comment_report.resolved;
    case "post":
      return i.post_report.resolved;
    case "private_message":
      return i.private_message_report.resolved;
    case "community":
      return i.community_report.resolved;
  }
}

function reportItemId(i: ReportCombinedView): number {
  switch (i.type_) {
    case "comment":
      return i.comment_report.id;
    case "post":
      return i.post_report.id;
    case "private_message":
      return i.private_message_report.id;
    case "community":
      return i.community_report.id;
  }
}

function reportTypeLabel(type_: ReportCombinedView["type_"]): string {
  switch (type_) {
    case "comment":
      return aimooT("type_comment");
    case "post":
      return aimooT("type_post");
    case "private_message":
      return aimooT("type_message");
    case "community":
      return aimooT("type_community");
  }
}
